import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { User, GraduationCap, Briefcase, MapPin, Save, Loader2, Plus, X, Heart } from 'lucide-react';
import './StudentProfile.css';

function StudentProfile() {
  const { t, i18n } = useTranslation();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const lang = i18n.language || 'ar';
  const isRTL = lang.startsWith('ar');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [universities, setUniversities] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [states, setStates] = useState([]);
  
  const [profile, setProfile] = useState({
    university_key: '',
    major_key: '',
    education_level: '',
    skills: [],
    interests: [],
    preferred_types: [],
    state_key: ''
  });
  
  const [skillInput, setSkillInput] = useState('');
  const [interestInput, setInterestInput] = useState('');

  useEffect(() => {
    if (!user || !token) {
      navigate('/welcome');
      return;
    }
    fetchReferenceData();
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token]);

  const fetchReferenceData = async () => {
    try {
      const [uniRes, subRes, stateRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/student-hub/universities`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/student-hub/subjects`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/student-hub/states`)
      ]);
      
      if (uniRes.ok) setUniversities(await uniRes.json());
      if (subRes.ok) setSubjects(await subRes.json());
      if (stateRes.ok) setStates(await stateRes.json());
    } catch (e) { console.error(e); }
  };

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/student-profile/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.profile) {
          setProfile(prev => ({ ...prev, ...data.profile }));
        }
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/student-profile`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(profile)
      });

      if (res.ok) {
        toast.success(t('profile.saved'));
      } else {
        toast.error(t('toast.error'));
      }
    } catch (e) {
      console.error(e);
      toast.error(t('toast.error'));
    }
    setSaving(false);
  };

  const addSkill = () => {
    if (skillInput.trim() && !profile.skills.includes(skillInput.trim())) {
      setProfile(prev => ({ ...prev, skills: [...prev.skills, skillInput.trim()] }));
      setSkillInput('');
    }
  };

  const removeSkill = (skill) => {
    setProfile(prev => ({ ...prev, skills: prev.skills.filter(s => s !== skill) }));
  };

  const addInterest = () => {
    if (interestInput.trim() && !profile.interests.includes(interestInput.trim())) {
      setProfile(prev => ({ ...prev, interests: [...prev.interests, interestInput.trim()] }));
      setInterestInput('');
    }
  };

  const removeInterest = (interest) => {
    setProfile(prev => ({ ...prev, interests: prev.interests.filter(i => i !== interest) }));
  };

  const togglePreferredType = (type) => {
    setProfile(prev => ({
      ...prev,
      preferred_types: prev.preferred_types.includes(type)
        ? prev.preferred_types.filter(t => t !== type)
        : [...prev.preferred_types, type]
    }));
  };

  if (loading) {
    return (
      <div className="student-profile-page" dir={isRTL ? 'rtl' : 'ltr'}>
        <header className="sp-header">
          <div className="sp-header-spacer" aria-hidden="true" />
          <h1 className="sp-title">{t('profile.studentTitle')}</h1>
        </header>
        <div className="sp-content">
          <Skeleton className="h-48 w-full mb-4" />
          <Skeleton className="h-48 w-full mb-4" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="student-profile-page" dir={isRTL ? 'rtl' : 'ltr'} data-testid="student-profile-page">
      {/* Header */}
      <header className="sp-header">
        <div className="sp-header-spacer" aria-hidden="true" />
        <h1 className="sp-title">{t('profile.studentTitle')}</h1>
      </header>

      {/* Main Content */}
      <main className="sp-content">
        {/* Section 1: Education */}
        <section className="sp-section" data-testid="education-section">
          <div className="sp-section-header">
            <GraduationCap size={20} className="sp-section-icon" />
            <h2 className="sp-section-title">{t('cvBuilder.sections.education')}</h2>
          </div>
          <div className="sp-section-body">
            {/* University */}
            <div className="sp-field">
              <Label className="sp-label">{t('profile.fields.university')}</Label>
              <Select 
                value={profile.university_key} 
                onValueChange={(v) => setProfile(p => ({ ...p, university_key: v }))}
              >
                <SelectTrigger className="sp-select" data-testid="university-select">
                  <SelectValue placeholder={t('profile.fields.university')} />
                </SelectTrigger>
                <SelectContent>
                  {universities.map(u => (
                    <SelectItem key={u.key} value={u.key}>
                      {lang.startsWith('ar') ? u.name_ar : u.name_en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Major */}
            <div className="sp-field">
              <Label className="sp-label">{t('profile.fields.major')}</Label>
              <Select 
                value={profile.major_key} 
                onValueChange={(v) => setProfile(p => ({ ...p, major_key: v }))}
              >
                <SelectTrigger className="sp-select" data-testid="major-select">
                  <SelectValue placeholder={t('profile.fields.major')} />
                </SelectTrigger>
                <SelectContent>
                  {subjects.map(s => (
                    <SelectItem key={s.key} value={s.key}>
                      {lang.startsWith('ar') ? s.name_ar : s.name_en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Education Level */}
            <div className="sp-field">
              <Label className="sp-label">{t('profile.fields.educationLevel')}</Label>
              <Select 
                value={profile.education_level} 
                onValueChange={(v) => setProfile(p => ({ ...p, education_level: v }))}
              >
                <SelectTrigger className="sp-select" data-testid="education-level-select">
                  <SelectValue placeholder={t('profile.fields.educationLevel')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="high_school">{t('profile.educationLevels.high_school')}</SelectItem>
                  <SelectItem value="bachelor">{t('profile.educationLevels.bachelor')}</SelectItem>
                  <SelectItem value="master">{t('profile.educationLevels.master')}</SelectItem>
                  <SelectItem value="phd">{t('profile.educationLevels.phd')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </section>

        {/* Section 2: Location */}
        <section className="sp-section" data-testid="location-section">
          <div className="sp-section-header">
            <MapPin size={20} className="sp-section-icon" />
            <h2 className="sp-section-title">{t('state')}</h2>
          </div>
          <div className="sp-section-body">
            <div className="sp-field">
              <Label className="sp-label">{t('profile.fields.location')}</Label>
              <Select 
                value={profile.state_key} 
                onValueChange={(v) => setProfile(p => ({ ...p, state_key: v }))}
              >
                <SelectTrigger className="sp-select" data-testid="state-select">
                  <SelectValue placeholder={t('selectState')} />
                </SelectTrigger>
                <SelectContent>
                  {states.map(s => (
                    <SelectItem key={s.key} value={s.key}>
                      {lang.startsWith('ar') ? s.name_ar : s.name_en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </section>

        {/* Section 3: Skills */}
        <section className="sp-section" data-testid="skills-section">
          <div className="sp-section-header">
            <User size={20} className="sp-section-icon" />
            <h2 className="sp-section-title">{t('profile.fields.skills')}</h2>
          </div>
          <div className="sp-section-body">
            <p className="sp-hint">{t('profile.skillsHint')}</p>
            <div className="sp-input-row">
              <Input
                value={skillInput}
                onChange={(e) => setSkillInput(e.target.value)}
                placeholder={t('profile.fields.skills')}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                className="sp-input"
                data-testid="skill-input"
              />
              <Button onClick={addSkill} variant="outline" size="icon" className="sp-add-btn" data-testid="add-skill-btn">
                <Plus size={16} />
              </Button>
            </div>
            <div className="sp-tags">
              {profile.skills.map((skill, index) => (
                <Badge key={index} variant="secondary" className="sp-tag">
                  {skill}
                  <button onClick={() => removeSkill(skill)} className="sp-tag-remove" data-testid={`remove-skill-${index}`}>
                    <X size={12} />
                  </button>
                </Badge>
              ))}
              {profile.skills.length === 0 && (
                <span className="sp-no-items">{t('noResults')}</span>
              )}
            </div>
          </div>
        </section>

        {/* Section 4: Interests */}
        <section className="sp-section" data-testid="interests-section">
          <div className="sp-section-header">
            <Heart size={20} className="sp-section-icon" />
            <h2 className="sp-section-title">{t('profile.fields.interests')}</h2>
          </div>
          <div className="sp-section-body">
            <p className="sp-hint">{t('profile.interestsHint')}</p>
            <div className="sp-input-row">
              <Input
                value={interestInput}
                onChange={(e) => setInterestInput(e.target.value)}
                placeholder={t('profile.fields.interests')}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addInterest())}
                className="sp-input"
                data-testid="interest-input"
              />
              <Button onClick={addInterest} variant="outline" size="icon" className="sp-add-btn" data-testid="add-interest-btn">
                <Plus size={16} />
              </Button>
            </div>
            <div className="sp-tags">
              {profile.interests.map((interest, index) => (
                <Badge key={index} variant="secondary" className="sp-tag">
                  {interest}
                  <button onClick={() => removeInterest(interest)} className="sp-tag-remove" data-testid={`remove-interest-${index}`}>
                    <X size={12} />
                  </button>
                </Badge>
              ))}
              {profile.interests.length === 0 && (
                <span className="sp-no-items">{t('noResults')}</span>
              )}
            </div>
          </div>
        </section>

        {/* Section 5: Preferred Opportunity Types */}
        <section className="sp-section" data-testid="preferred-types-section">
          <div className="sp-section-header">
            <Briefcase size={20} className="sp-section-icon" />
            <h2 className="sp-section-title">{t('profile.fields.preferredTypes')}</h2>
          </div>
          <div className="sp-section-body">
            <div className="sp-type-grid">
              {['job', 'internship', 'volunteer'].map(type => (
                <button
                  key={type}
                  className={`sp-type-btn ${profile.preferred_types.includes(type) ? 'active' : ''}`}
                  onClick={() => togglePreferredType(type)}
                  data-testid={`type-${type}`}
                >
                  {t(`opps.type.${type}`)}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Save Button */}
        <div className="sp-save-wrapper">
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="sp-save-btn"
            data-testid="save-profile-btn"
          >
            {saving ? (
              <><Loader2 size={18} className="sp-spin" /> {t('profile.saving')}</>
            ) : (
              <><Save size={18} /> {t('profile.save')}</>
            )}
          </Button>
        </div>
      </main>
    </div>
  );
}

export default StudentProfile;
